import Constants from 'expo-constants';

import { logger } from '@/utils/logger';

/**
 * The API origin and URL joining, kept in a leaf module so auth-service can build URLs
 * without importing api-service — which imports auth-service back, a load-order TDZ crash
 * (`tests/frontend/services/services-import-cycles.spec.ts`).
 */

const resolveApiBaseUrl = () => {
	// Web: ensure preview site always targets the preview API, even if the build env was misconfigured.
	const webHostname = globalThis?.location?.hostname;
	if (typeof webHostname === 'string' && webHostname.length > 0) {
		const hostname = webHostname.toLowerCase();
		if (
			hostname === 'test.propertyworkorder.app'
			|| hostname.endsWith('.test.propertyworkorder.app')
			|| hostname.startsWith('preview.')
		) {
			return 'https://test-api.propertyworkorder.app/';
		}
	}

	if (process.env.EXPO_PUBLIC_API_BASE_URL) {
		console.debug('process.env.EXPO_PUBLIC_API_BASE_URL', process.env.EXPO_PUBLIC_API_BASE_URL);
		return process.env.EXPO_PUBLIC_API_BASE_URL;
	}

	const extraEnv = Constants.expoConfig?.extra?.EXPO_PUBLIC_API_BASE_URL;
	if (typeof extraEnv === 'string' && extraEnv.length > 0) {
		console.debug('extraEnv', extraEnv);
		return extraEnv;
	}

	const legacyExtra = (Constants.manifest as { extra?: Record<string, unknown> } | null)?.extra?.EXPO_PUBLIC_API_BASE_URL;
	if (typeof legacyExtra === 'string' && legacyExtra.length > 0) {
		console.debug('legacyExtra', legacyExtra);
		return legacyExtra;
	}

	return 'https://api.propertyworkorder.app/';
};

export const API_BASE_URL = resolveApiBaseUrl();
logger.log('API_BASE_URL', API_BASE_URL);

/**
 * Join the API base URL to a request path, normalising BOTH halves (#444).
 *
 * Bare concatenation made correctness depend on an unwritten convention — base ends with
 * `/`, paths do not start with one — and neither half of it held. Most call sites pass a
 * leading slash, yielding `https://host//search`, which servers tolerate; that tolerance is
 * why it went unnoticed. The admin screens pass `admin/...` with no slash and worked only
 * because every BUILT-IN base happens to end with one.
 *
 * `EXPO_PUBLIC_API_BASE_URL` is a plain env var, so anyone pointing the app at a local
 * Worker, a tunnel or a preview host writes it the way URLs are normally written — without a
 * trailing slash. That produced `http://localhost:8887admin/backup`: not a clean
 * configuration error but an opaque `Failed to parse URL` inside fetch, on a few screens
 * only, which reads like a permissions bug rather than a URL one.
 *
 * An absolute URL is deliberately NOT honoured as a destination. `fetchApi` attaches the
 * caller's device token, so following one would hand credentials to an arbitrary origin; no
 * call site passes one, and treating it as an odd path 404s against our own API instead.
 */
export const joinApiUrl = (base: string, path: string): string => {
	const trimmedBase = base.replace(/\/+$/, '');
	if (!path) return `${trimmedBase}/`;
	return `${trimmedBase}${path.startsWith('/') ? path : `/${path}`}`;
};
